import React from "react";
import { Box } from "@UI/Components/Box/Box";
import { Text } from "@UI/Components/Text/Text";
import { Rating } from "@UI/Components/Rating/Rating";
import { CategoryPill } from "@UI/Components/CategoryPill/CategoryPill";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import {
  RecommendationCard,
  RecommendationCardProps,
} from "./RecommendationCard";
import { IRecommendedServiceProvider } from "../../types/recommendations.types";
import messages from "./messages";

export type SPRecommendationCardProps = IRecommendedServiceProvider & {
  /** Override the cover image height. Defaults to "140px". */
  coverHeight?: RecommendationCardProps["coverHeight"];
};

/**
 * SPRecommendationCard
 *
 * A RecommendationCard pre-wired for a recommended Service Provider.
 * Accepts the same shape as IRecommendedServiceProvider from the API.
 *
 * Layout:
 *   [avatar image]
 *   header: [name (bold, left)] [★ rating/5 (right)]
 *   body:   review quote + "Reviewed by author"
 *   footer: CategoryPill (category_name + category_key)
 */
export const SPRecommendationCard = ({
  name,
  avatar,
  category,
  cta_path,
  coverHeight = "140px",
}: SPRecommendationCardProps) => {
  const trackedHref = cta_path
    ? `${cta_path}${
        cta_path.includes("?") ? "&" : "?"
      }from=home_listposter_sp_recommendation`
    : undefined;

  return (
    <RecommendationCard
      coverImage={avatar.normal}
      coverFallbackSrc={require("assets/images/no_avatar_small.svg")}
      coverAlt={name}
      coverHeight={coverHeight}
      href={trackedHref}
      header={
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box fontFamily="heading" fontSize="xl" color="gray.800">
            {name}
          </Box>
          <Rating rating={category.average_rating} />
        </Box>
      }
      footer={
        <Box display="flex" justifyContent="center" width="100%">
          <CategoryPill
            name={category.s_category_name || category.p_category_name}
            categoryKey={category.p_category_key}
          />
        </Box>
      }
    >
      {category.featured_evaluation && (
        <>
          <Text fontSize="xs" color="gray.800" noOfLines={2} fontStyle="italic">
            {category.featured_evaluation.comment}
          </Text>
          <Text fontSize="xs" color="gray.600" fontStyle="italic">
            <FormattedMessage
              {...messages.reviewedBy}
              values={{ authorName: category.featured_evaluation.author_name }}
            />
          </Text>
        </>
      )}
    </RecommendationCard>
  );
};
